// PayPalDialog.js
import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, Box, IconButton, DialogActions, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PayPalButton from "./PayPalButton";
import PaymentReceipt from "./PaymentReceipt";
import { Button } from '@mui/material';

export default function PayPalDialog({ open, 
                                      onClose, 
                                      amount, 
                                      onPaymentSuccess }) {
  const [ready, setReady] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null); // {status:'success'|'error', ...}
  const [showReceipt, setShowReceipt] = useState(false);
  const focusRef = useRef(null);

  const handleClose = () => {
    // remove focus from any active element to avoid aria-hidden warnings when dialog closes
    try {
      if (document && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (e) {
      // ignore
    }
    if (onClose) onClose();
  };

  // (no debug logging)

	// Notify parent of successful payment
  // Notify parent of successful payment. Pass an ISO expiration/renewal date so parent
  // can treat this the same way as a free-trial success handler expects (an ISO date string).
  useEffect(() => {
    if (paymentResult?.status === 'success') {
      try {
        // Prefer server-provided renewal date (returned by capture endpoint)
        const serverRenewal = paymentResult.renewal_date || paymentResult.data?.renewal_date || paymentResult.data?.renewalDate;
        if (serverRenewal) {
          console.log('PayPalDialog calling onPaymentSuccess with renewal:', serverRenewal);
          onPaymentSuccess?.(serverRenewal);
          return;
        }

        // Fallback: Try to extract capture update_time from PayPal response and compute expiration
        const updateTime = paymentResult.data?.purchase_units?.[0]?.payments?.captures?.[0]?.update_time || paymentResult.data?.update_time;
        const baseDate = updateTime ? new Date(updateTime) : new Date();
        baseDate.setMonth(baseDate.getMonth() + 1);
        const computedRenewal = baseDate.toISOString();
        console.log('PayPalDialog calling onPaymentSuccess with computed renewal:', computedRenewal);
        onPaymentSuccess?.(computedRenewal);
      } catch (e) {
        // Ensure failures don't break the app; still notify parent with undefined
        onPaymentSuccess?.(undefined);
      }
    }
  }, [paymentResult, onPaymentSuccess]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      // Only close the dialog on explicit close actions. Ignore backdrop clicks and escape key.
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      disableEscapeKeyDown
      // On small screens use a full-screen dialog so iframes/inputs have room and can be scrolled
      fullScreen={fullScreen}
      // Render inline on small screens so the dialog participates in normal page scrolling
      disablePortal={fullScreen}
      // Relax focus enforcement so embedded iframes (PayPal) can receive focus/scroll on iOS
      disableEnforceFocus
      // Fixed, responsive size so the dialog doesn't open as a narrow stripe and then grow
  PaperProps={{ sx: { 
    borderRadius: fullScreen ? 0 : '12px', 
    width: fullScreen ? '100vw' : 'min(540px, 92vw)', 
    minHeight: fullScreen ? '100dvh' : 240, 
    paddingTop: fullScreen ? 'env(safe-area-inset-top)' : undefined,
		// height: fullScreen ? '100dvh' : 'auto',
		// height:'100%',
    overflow: 'visible'          // allow iframe to expand if needed
  } }}
      keepMounted
      disableRestoreFocus
      TransitionProps={{
        onEntered: () => {
          setReady(true);
          focusRef.current?.focus?.(); // move focus into the dialog
        },
        onExited: () => setReady(false),
      }}
      fullWidth
      maxWidth="sm"
    >
      {/* Keep the title area pinned and padded for safe-area on mobile */}
      <DialogTitle sx={{ fontWeight: 'bold', 
                          position: 'relative', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          pt: fullScreen ? 'env(safe-area-inset-top)' : 1, zIndex: 2 }}>
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          {paymentResult?.status === 'success'
            ? `Your Payment Completed — ${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amount)}`
            : `Complete Your Payment — ${new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amount)}`}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: fullScreen ? 'calc(env(safe-area-inset-top) + 8px)' : 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {/* Title is pinned; the rest of the content is scrollable below */}
  <DialogContent sx={{
    minHeight: 200,
    display: 'block',
    py: 2,
  overflowX: 'visible',        // don't clip horizontally
    overflowY: 'auto',          // still allow vertical scrolling
    WebkitOverflowScrolling: 'touch',
    boxSizing: 'border-box',
  px: fullScreen ? 0 : 2,
  ...(fullScreen ? { pt: 'env(safe-area-inset-top)', pb: 'env(safe-area-inset-bottom)' } : {})
  }}>
        {/* focus sink */}
        <Box tabIndex={-1} ref={focusRef} sx={{ outline: "none" }} />

        {/* Render PayPal buttons as soon as dialog is open (don't strictly wait for transition).
            Waiting for the transition can sometimes prevent the buttons mounting in certain nested
            modal/portal configurations, so prefer rendering when `open` is true. */}
        {open && !paymentResult && (
          <PayPalButton
            key={amount}
            amount={amount}
            isOpen={open}
            renderDelayMs={150}
            onPaymentResult={(res) => setPaymentResult(res)}
          />
        )}

        {/* Payment result UI */}
        {paymentResult && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paymentResult.status === 'success' ? (
              <Box>
                <Box sx={{ fontWeight: 'bold', mb: 1 }}>Payment successful, thank you! ✅</Box>
                <Box>Order ID: {paymentResult.data?.id || paymentResult.data?.orderId}</Box>
                <Box>Amount: {paymentResult.data?.purchase_units?.[0]?.payments?.captures?.[0]?.amount?.value || ''} {paymentResult.data?.purchase_units?.[0]?.amount?.currency_code || ''}</Box>
              </Box>
            ) : (
              <Box>
                <Box sx={{ fontWeight: 'bold', mb: 1 }}>Payment failed, sorry! ⚠️</Box>
                <Box>{paymentResult.message || 'An error occurred during payment.'}</Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {/* <Button variant="outlined" onClick={() => { setPaymentResult(null); }}>Close</Button> */}
              {paymentResult.status === 'success' && !showReceipt && (
                <Button variant="contained" onClick={() => setShowReceipt(true)}>View Receipt</Button>
              )}
            </Box>
            {/* Render receipt component in-dialog when requested */}
            {showReceipt && paymentResult.status === 'success' && (
              <PaymentReceipt paymentData={paymentResult.data} onClose={() => setShowReceipt(false)} />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pr: 2, pb: 2 }}>
        <Button variant="text" onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
// PayPalDialog.js